/**
 * BoardGrid — renders the tile layout as a responsive grid.
 *
 * Implements glance-grammar-spec §2 grid system:
 * - §2.1: three tile sizes (1×1, 2×1, 2×2), grid-column + grid-row spans.
 * - §2.2: breakpoints (desktop ≥ 1280: 8 cols, narrow 1024-1279: 6, tablet
 *   768-1023: 4, phone <768: 2). Cell size fixed per breakpoint.
 * - §2.3: reflow rules — 2×2 collapses to 2×1 on phone; tiles never rearranged,
 *   only wrapped (spatial memory is sacred).
 *
 * Layout comes from the Board's layout array; positions are absolute grid placements.
 *
 * Immutability: pure component, no mutation.
 *
 * Fix D5b: tileById is now memoised to avoid rebuilding the Map on every render.
 */
import { useMemo } from 'react';
import type { Board } from 'briefpane/schema';
import type { CacheClient, GlanceViewModel, TileRegistry } from 'briefpane/contracts';
import { TileCell } from './TileCell.js';
import type { CrossfadeMap } from '../App.js';

interface BoardGridProps {
  readonly board: Board;
  readonly cache: CacheClient;
  readonly registry: TileRegistry;
  /** Optional: if true, the grid renders in edit mode (drag handles visible, etc). */
  readonly editMode?: boolean;
  readonly onTileClick?: (tileId: string) => void;
  /** Called when the drag handle receives a pointer-down (or keyboard activate) on a tile. */
  readonly onDragStart?: (tileId: string, e: React.PointerEvent | React.KeyboardEvent) => void;
  /** Called when the user confirms tile removal (two-tap). */
  readonly onRemove?: (tileId: string) => void;
  /** Called when the user selects a new size for a tile in edit mode. */
  readonly onResize?: (tileId: string, size: import('briefpane/schema').TileSize) => void;
  /** Called when the SE-corner resize handle receives a pointer-down. */
  readonly onResizeStart?: (
    tileId: string,
    currentSize: import('briefpane/schema').TileSize,
    allowedSizes: readonly import('briefpane/schema').TileSize[],
    e: React.PointerEvent,
  ) => void;
  /** Per-tile crossfade role map for template swap animations (core-flows §2.2). */
  readonly crossfadeMap?: CrossfadeMap;
  /**
   * Optional shared ref registry for live GlanceViewModels.
   * TileCell writes its latest vm here after each render; App reads it on tile click.
   * See TileCell for full rationale.
   */
  readonly vmRegistry?: React.MutableRefObject<Map<string, { vm: GlanceViewModel; data?: Record<string, unknown> }>>;
  /** Whether a drag is currently active (for jiggle pause + vacancy state). */
  readonly isDragging?: boolean;
  /** The tileId being dragged (for jiggle pause + vacancy on specific tile). */
  readonly dragTileId?: string | null;
  /** Called to enter edit mode via long-press (§2.1 D-016). */
  readonly onEditModeEnter?: () => void;
}

/**
 * Map a layout w/h pair to CSS grid-column-span and grid-row-span.
 * The schema allows w: 1..6, h: 1..6; glance-grammar spec uses 1, 2 only.
 */
function sizeToSpan(w: number, h: number): { colSpan: number; rowSpan: number } {
  return { colSpan: Math.min(w, 8), rowSpan: Math.min(h, 6) };
}

export function BoardGrid({ board, cache, registry, editMode = false, onTileClick, onDragStart, onRemove, onResize, onResizeStart, crossfadeMap, vmRegistry, isDragging = false, dragTileId = null, onEditModeEnter }: BoardGridProps): React.ReactElement {
  // D5b: memoised Map from tileId → tile for O(1) lookup. Only rebuilt when board.tiles changes.
  const tileById = useMemo(
    () => new Map(board.tiles.map((t) => [t.id, t])),
    [board.tiles],
  );

  return (
    <div
      // Stable hook for the grid CSS (review MED-1): all grid GEOMETRY lives in
      // the stylesheet (.board-grid in index.css), NOT inline — an inline
      // grid-template-columns can never be overridden by the TV-mode rule
      // (inline beats any selector), which left the 6×200px TV grid dead until
      // the E2E guard caught it. Only the board-config-driven gap crosses the
      // boundary, as a CSS custom property.
      className="board-grid"
      style={{ '--board-gap': `${board.grid.gap}px` } as React.CSSProperties}
    >
      {board.layout.length === 0 && !editMode && (
        <div
          style={{
            gridColumn: '1 / -1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
            color: 'var(--color-text-dim)',
            fontSize: 'var(--text-delta)',
            textAlign: 'center',
            padding: 'var(--space-4)',
          }}
        >
          This space is empty — press Edit to add tiles
        </div>
      )}
      {board.layout.map((entry, idx) => {
        const tile = tileById.get(entry.tileId);
        if (!tile) return null;
        const { colSpan, rowSpan } = sizeToSpan(entry.w, entry.h);

        // Crossfade: 'entering' fades in, 'leaving' fades out, 'stable' no animation.
        const crossfadeRole = crossfadeMap?.get(entry.tileId);
        const crossfadeClass = crossfadeRole ? `tile-crossfade--${crossfadeRole}` : undefined;
        const cellClassName = [
          editMode ? 'board-cell--edit' : undefined,
          crossfadeClass,
        ].filter(Boolean).join(' ') || undefined;

        return (
          <div
            key={entry.tileId}
            data-tile-id={entry.tileId}
            className={cellClassName}
            tabIndex={editMode ? 0 : undefined}
            style={{
              gridColumn: `span ${colSpan}`,
              gridRow: `span ${rowSpan}`,
              position: 'relative',
              /*
               * Phone reflow (§2.3): on very narrow viewports, 2×2 → max 2 cols.
               * We handle this in the CSS grid natural reflow — the auto-fill
               * already wraps tiles that exceed the available cols.
               * Additional CSS in index.css handles secondary-content hiding on phone.
               */
            }}
            onClick={onTileClick ? () => onTileClick(entry.tileId) : undefined}
          >
            {/*
             * H1/M4 SINGLE-OWNER TRANSFORM FIX:
             * [data-tile-id] is owned EXCLUSIVELY by FLIP animation (use-flip-animation.ts).
             * Displacement preview (push-aside during drag) is applied to this INNER
             * .tile-displace wrapper — a nested element that FLIP never touches.
             * This ensures FLIP and displacement never clobber each other's transform.
             *
             * DROP SEQUENCE:
             * 1. pointerdown → drag starts, containerRect captured once.
             * 2. pointermove → computeDisplacements() → setDisplacements state.
             * 3. useEffect reads displacements → writes translate(dx,dy) to .tile-displace children.
             *    FLIP's element ([data-tile-id]) is untouched — its transform is clear.
             * 4. pointerup → commitMoveTiles (if displacements) or commitMove (no displacements).
             *    BEFORE commit: flipCaptureRef.current(dragTileId) snapshots [data-tile-id] rects.
             * 5. State update → BoardGrid re-renders with new layout.
             * 6. useLayoutEffect (FLIP): reads new rects on [data-tile-id], computes inverted
             *    transform, applies transition on [data-tile-id]. These tiles move from old→new.
             * 7. Drag ends → setDisplacements(empty) → .tile-displace transforms cleared.
             *    FLIP takes over visual from this point — no conflict because the owners are separate.
             */}
            <div className="tile-displace" style={{ height: '100%' }}>
            <TileCell
              tile={tile}
              cache={cache}
              registry={registry}
              staggerIndex={idx}
              editMode={editMode}
              {...(onDragStart ? { onDragStart } : {})}
              {...(onRemove ? { onRemove: () => onRemove(entry.tileId) } : {})}
              {...(onResize ? { onResize: (size) => onResize(entry.tileId, size) } : {})}
              {...(onResizeStart ? { onResizeStart } : {})}
              {...(vmRegistry ? { vmRegistry } : {})}
              isDragging={isDragging}
              dragTileId={dragTileId}
              {...(onEditModeEnter ? { onEditModeEnter } : {})}
            />
            </div>
          </div>
        );
      })}
    </div>
  );
}
